import { useContext, useMemo } from 'react';
import {
  type TriageRun,
  triageRunHasVisibleActions,
} from 'src/graphql/entities/conversation-triage-run';
import {
  ChildStatus,
  MessageTypeId,
  MessageTypeRegistration,
  TimelineEntry,
} from 'src/pages/Conversations/Chat/registry/types';
import {
  DEFAULT_TIMEOUT_MS,
  useReadinessWithTimeout,
} from 'src/pages/Conversations/Chat/registry/use-readiness-with-timeout';
import { TriageRunContext } from './triage-run-context';
import { TriageRunProvider } from './triage-run-provider';
import { TriageRunRow } from './triage-run-row';

function useVisibleRuns(): TriageRun[] {
  const runs = useContext(TriageRunContext)?.runs;

  return useMemo<TriageRun[]>(
    () => (runs ?? []).filter(triageRunHasVisibleActions),
    [runs],
  );
}

function useItems(): TimelineEntry<TriageRun>[] {
  const runs = useVisibleRuns();

  return useMemo<TimelineEntry<TriageRun>[]>(
    () =>
      runs.map((run) => ({
        type: MessageTypeId.TriageRun,
        timestamp: run.timestamp,
        item: run,
      })),
    [runs],
  );
}

function useReadiness(): ChildStatus {
  const context = useContext(TriageRunContext);
  const isLoading = context?.isLoading ?? false;
  const error = context?.error;
  const runs = context?.runs ?? [];

  // Ready resolves as soon as the (lightweight) query returns — including the
  // common empty case — so a blocking source never strands non-triage threads.
  return useReadinessWithTimeout({
    loading: isLoading,
    error,
    hasData: runs.length > 0,
    timeoutMs: DEFAULT_TIMEOUT_MS,
  });
}

export const triageRunRegistration: MessageTypeRegistration<TriageRun> = {
  id: MessageTypeId.TriageRun,
  Row: TriageRunRow,
  Provider: TriageRunProvider,
  getKey: (item) => item.id,
  useItems,
  useReadiness,
  blockingForInitialRender: true,
  timeoutMs: DEFAULT_TIMEOUT_MS,
};

export default triageRunRegistration;
